(() => {
  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => [...scope.querySelectorAll(selector)];

  const escapeHTML = (value = "") =>
    String(value).replace(/[&<>"']/g, (character) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#039;",
    })[character]);

  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || "PKR";
    try {
      return new Intl.NumberFormat("en-PK", {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
      }).format(cents / 100);
    } catch {
      return `Rs.${Math.round(cents / 100).toLocaleString("en-PK")}`;
    }
  };

  const lockPage = (locked) => document.body.classList.toggle("is-locked", locked);

  const showToast = (message) => {
    const toast = $("[data-toast]");
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add("is-visible");
    window.clearTimeout(showToast.timeout);
    showToast.timeout = window.setTimeout(() => toast.classList.remove("is-visible"), 2800);
  };

  const setDrawer = (drawer, open) => {
    if (!drawer) return;
    drawer.classList.toggle("is-open", open);
    drawer.setAttribute("aria-hidden", open ? "false" : "true");
    lockPage(open);
  };

  const initDrawers = () => {
    const cartDrawer = $("[data-cart-drawer]");
    const searchDrawer = $("[data-search-drawer]");
    const mobileMenu = $("[data-mobile-menu]");

    $$("[data-cart-open]").forEach((button) => {
      button.addEventListener("click", () => setDrawer(cartDrawer, true));
    });
    $$("[data-drawer-close]").forEach((button) => {
      button.addEventListener("click", () => setDrawer(cartDrawer, false));
    });
    $$("[data-search-open]").forEach((button) => {
      button.addEventListener("click", () => {
        setDrawer(searchDrawer, true);
        window.setTimeout(() => $("#DrawerSearch")?.focus(), 260);
      });
    });
    $$("[data-search-close]").forEach((button) => {
      button.addEventListener("click", () => setDrawer(searchDrawer, false));
    });
    $$("[data-mobile-menu-open]").forEach((button) => {
      button.addEventListener("click", () => setDrawer(mobileMenu, true));
    });
    $$("[data-mobile-menu-close]").forEach((button) => {
      button.addEventListener("click", () => setDrawer(mobileMenu, false));
    });

    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      setDrawer(cartDrawer, false);
      setDrawer(searchDrawer, false);
      setDrawer(mobileMenu, false);
      const filters = $("[data-filters]");
      filters?.classList.remove("is-open");
    });
  };

  const renderCart = (cart) => {
    $$("[data-header-cart-count]").forEach((element) => {
      element.textContent = cart.item_count;
    });
    $$("[data-cart-count]").forEach((element) => {
      element.textContent = `(${cart.item_count})`;
    });

    const itemsContainer = $("[data-cart-items]");
    const footer = $("[data-cart-footer]");
    const total = $("[data-cart-total]");
    if (!itemsContainer) return;

    if (!cart.items.length) {
      itemsContainer.innerHTML = `
        <div class="empty-state">
          <p>Your bag is waiting for something beautiful.</p>
          <a class="button button--outline" href="/collections/all">Shop collection</a>
        </div>`;
      footer?.classList.add("hidden");
      return;
    }

    itemsContainer.innerHTML = cart.items.map((item, index) => `
      <div class="mini-cart-item" data-line="${index + 1}">
        <a href="${escapeHTML(item.url)}">
          <img src="${escapeHTML(item.image || "")}" width="100" height="134" alt="${escapeHTML(item.product_title)}">
        </a>
        <div>
          <a class="mini-cart-item__title" href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a>
          <span>${formatMoney(item.final_line_price)}</span>
          <div class="quantity">
            <button type="button" data-cart-change="${index + 1}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button>
            <span>${item.quantity}</span>
            <button type="button" data-cart-change="${index + 1}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
          </div>
        </div>
      </div>
    `).join("");

    footer?.classList.remove("hidden");
    if (total) total.textContent = formatMoney(cart.total_price);
  };

  const fetchCart = async () => {
    const response = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error("Unable to load bag");
    const cart = await response.json();
    renderCart(cart);
    return cart;
  };

  const addFormToCart = async (form) => {
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: { Accept: "application/json" },
      body: new FormData(form),
    });
    if (!response.ok) {
      let message = "This item could not be added";
      try {
        const error = await response.json();
        message = error.description || error.message || message;
      } catch {
        // Keep the shopper-friendly fallback message.
      }
      throw new Error(message);
    }
    return response.json();
  };

  const initCart = () => {
    document.addEventListener("submit", async (event) => {
      const form = event.target.closest("[data-quick-add], [data-product-form]");
      if (!form) return;
      const submitter = event.submitter || form.querySelector('[type="submit"]');
      const isQuickAdd = form.matches("[data-quick-add]");
      const isMainAddButton = submitter?.classList.contains("product-form__submit");
      if (!isQuickAdd && !isMainAddButton) return;
      event.preventDefault();

      const submit = submitter;
      const originalText = submit?.textContent;
      if (submit) {
        submit.disabled = true;
        submit.textContent = "Adding…";
      }

      try {
        await addFormToCart(form);
        await fetchCart();
        setDrawer($("[data-cart-drawer]"), true);
        showToast("Added to your bag");
      } catch (error) {
        showToast(error.message);
      } finally {
        if (submit) {
          submit.disabled = false;
          submit.textContent = originalText;
        }
      }
    });

    document.addEventListener("click", async (event) => {
      const buyNowButton = event.target.closest("[data-buy-now]");
      if (buyNowButton) {
        const form = buyNowButton.closest("[data-product-form]");
        if (!form || buyNowButton.disabled) return;
        const variantId =
          buyNowButton.dataset.variantId ||
          $('input[name="id"]', form)?.value;
        const quantity = Math.max(
          1,
          Number($('input[name="quantity"]', form)?.value || 1)
        );
        if (!variantId) {
          showToast("Please refresh the page and try again");
          return;
        }

        // A cart permalink creates the Shopify checkout directly. Opening it
        // at the top level also works from inside Shopify's theme editor.
        const root = window.Shopify?.routes?.root || "/";
        const checkoutUrl = `${root}cart/${encodeURIComponent(variantId)}:${quantity}`;
        window.open(checkoutUrl, "_top");
        return;
      }

      const changeButton = event.target.closest("[data-cart-change]");
      if (!changeButton) return;
      changeButton.disabled = true;
      try {
        const response = await fetch("/cart/change.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            line: Number(changeButton.dataset.cartChange),
            quantity: Number(changeButton.dataset.quantity),
          }),
        });
        if (!response.ok) throw new Error("Bag could not be updated");
        renderCart(await response.json());
      } catch (error) {
        showToast(error.message);
        changeButton.disabled = false;
      }
    });
  };

  const initCountdowns = (scope = document) => {
    $$("[data-countdown]", scope).forEach((countdown) => {
      if (countdown.dataset.initialized) return;
      countdown.dataset.initialized = "true";
      const target = new Date(countdown.dataset.countdown.replace(" ", "T")).getTime();
      if (Number.isNaN(target)) return;

      const update = () => {
        const distance = Math.max(0, target - Date.now());
        const days = Math.floor(distance / 86400000);
        const hours = Math.floor((distance % 86400000) / 3600000);
        const minutes = Math.floor((distance % 3600000) / 60000);
        const seconds = Math.floor((distance % 60000) / 1000);
        $("[data-days]", countdown).textContent = String(days).padStart(2, "0");
        $("[data-hours]", countdown).textContent = String(hours).padStart(2, "0");
        $("[data-minutes]", countdown).textContent = String(minutes).padStart(2, "0");
        $("[data-seconds]", countdown).textContent = String(seconds).padStart(2, "0");
      };
      update();
      countdown._timer = window.setInterval(update, 1000);
    });
  };

  const initSliders = (scope = document) => {
    $$("[data-slider]", scope).forEach((slider) => {
      if (slider.dataset.initialized) return;
      slider.dataset.initialized = "true";
      const slides = $$("[data-slide]", slider);
      const dots = $$("[data-slide-to]", slider);
      if (slides.length < 2) return;
      let index = 0;

      const goTo = (next) => {
        index = (next + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === index;
          slide.classList.toggle("is-active", active);
          slide.setAttribute("aria-hidden", active ? "false" : "true");
        });
        dots.forEach((dot, dotIndex) => dot.classList.toggle("is-active", dotIndex === index));
      };

      dots.forEach((dot) => dot.addEventListener("click", () => goTo(Number(dot.dataset.slideTo))));
      const delay = Number(slider.dataset.autoplay);
      if (delay > 0 && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        slider._timer = window.setInterval(() => goTo(index + 1), delay);
      }
    });
  };

  const initCarousels = (scope = document) => {
    $$("[data-carousel]", scope).forEach((carousel) => {
      if (carousel.dataset.carouselInitialized) return;
      carousel.dataset.carouselInitialized = "true";
      const track = $("[data-carousel-track]", carousel);
      if (!track) return;
      const items = [...track.children];
      if (items.length < 2) return;

      const mobileQuery = window.matchMedia("(max-width: 749px)");
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      const delay = Math.max(1000, Number(carousel.dataset.mobileAutoplay) || 3400);
      const controlsHost = $(".page-width", carousel) || carousel;
      let index = 0;
      let interval;
      let resumeTimer;
      const initialBounds = carousel.getBoundingClientRect();
      let inView = initialBounds.bottom > 0 && initialBounds.top < window.innerHeight;
      let userPaused = false;
      let mediaPaused = false;
      let scrollFrame;

      const mobileControls = document.createElement("div");
      mobileControls.className = "mobile-carousel-ui";
      mobileControls.innerHTML = `
        <button type="button" class="mobile-carousel-ui__button" data-mobile-carousel-prev aria-label="Previous item">‹</button>
        <div class="mobile-carousel-ui__progress" aria-hidden="true"><span></span></div>
        <span class="mobile-carousel-ui__count" aria-hidden="true">01 / ${String(items.length).padStart(2, "0")}</span>
        <button type="button" class="mobile-carousel-ui__button mobile-carousel-ui__pause" data-mobile-carousel-pause aria-label="Pause automatic rotation">Ⅱ</button>
        <button type="button" class="mobile-carousel-ui__button" data-mobile-carousel-next aria-label="Next item">›</button>`;
      controlsHost.appendChild(mobileControls);

      const progress = $(".mobile-carousel-ui__progress span", mobileControls);
      const count = $(".mobile-carousel-ui__count", mobileControls);
      const pauseButton = $("[data-mobile-carousel-pause]", mobileControls);

      const itemPosition = (item) => {
        const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
        return Math.max(0, Math.min(item.offsetLeft - track.offsetLeft, maximum));
      };
      const nearestIndex = () => {
        let nearest = 0;
        let distance = Number.POSITIVE_INFINITY;
        items.forEach((item, itemIndex) => {
          const nextDistance = Math.abs(itemPosition(item) - track.scrollLeft);
          if (nextDistance < distance) {
            nearest = itemIndex;
            distance = nextDistance;
          }
        });
        return nearest;
      };

      const updateControls = () => {
        index = nearestIndex();
        const value = (index + 1) / items.length;
        progress?.style.setProperty("--carousel-progress", String(value));
        if (count) count.textContent = `${String(index + 1).padStart(2, "0")} / ${String(items.length).padStart(2, "0")}`;
      };

      const goTo = (nextIndex) => {
        index = (nextIndex + items.length) % items.length;
        track.scrollTo({ left: itemPosition(items[index]), behavior: "smooth" });
        updateControls();
      };

      const stop = () => {
        window.clearInterval(interval);
        interval = undefined;
      };

      const start = () => {
        stop();
        if (!mobileQuery.matches || reducedMotion.matches || !inView || userPaused || mediaPaused || document.hidden) return;
        interval = window.setInterval(() => goTo(nearestIndex() + 1), delay);
      };

      const pauseTemporarily = () => {
        stop();
        window.clearTimeout(resumeTimer);
        if (!userPaused) resumeTimer = window.setTimeout(start, 4000);
      };

      const previous = () => {
        pauseTemporarily();
        goTo(nearestIndex() - 1);
      };

      const next = () => {
        pauseTemporarily();
        goTo(nearestIndex() + 1);
      };

      $$("[data-carousel-prev]", carousel).forEach((button) => button.addEventListener("click", previous));
      $$("[data-carousel-next]", carousel).forEach((button) => button.addEventListener("click", next));
      $("[data-mobile-carousel-prev]", mobileControls)?.addEventListener("click", previous);
      $("[data-mobile-carousel-next]", mobileControls)?.addEventListener("click", next);
      pauseButton?.addEventListener("click", () => {
        userPaused = !userPaused;
        pauseButton.textContent = userPaused ? "▶" : "Ⅱ";
        pauseButton.setAttribute("aria-label", userPaused ? "Resume automatic rotation" : "Pause automatic rotation");
        if (userPaused) stop();
        else start();
      });

      let gestureStart;
      track.addEventListener("pointerdown", (event) => {
        gestureStart = { x: event.clientX, y: event.clientY };
      }, { passive: true });
      track.addEventListener("pointerup", (event) => {
        if (!gestureStart) return;
        const horizontal = Math.abs(event.clientX - gestureStart.x);
        const vertical = Math.abs(event.clientY - gestureStart.y);
        gestureStart = undefined;
        if (horizontal > 28 && horizontal > vertical) pauseTemporarily();
      }, { passive: true });
      track.addEventListener("pointercancel", () => {
        gestureStart = undefined;
      }, { passive: true });
      track.addEventListener("wheel", (event) => {
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY) && Math.abs(event.deltaX) > 8) {
          pauseTemporarily();
        }
      }, { passive: true });
      track.addEventListener("scroll", () => {
        window.cancelAnimationFrame(scrollFrame);
        scrollFrame = window.requestAnimationFrame(updateControls);
      }, { passive: true });
      track.addEventListener("focusin", pauseTemporarily);
      carousel.addEventListener("carousel:media-play", () => {
        mediaPaused = true;
        stop();
      });
      carousel.addEventListener("carousel:media-pause", () => {
        mediaPaused = false;
        start();
      });

      const observer = new IntersectionObserver(([entry]) => {
        inView = entry.isIntersecting;
        if (inView) start();
        else stop();
      }, { threshold: 0.15 });
      observer.observe(carousel);

      mobileQuery.addEventListener("change", start);
      reducedMotion.addEventListener("change", start);
      document.addEventListener("visibilitychange", start);
      updateControls();
      start();
    });
  };

  const initVideoCoverflows = (scope = document) => {
    $$("[data-video-coverflow], [data-coverflow]", scope).forEach((coverflow) => {
      if (coverflow.dataset.coverflowInitialized) return;
      coverflow.dataset.coverflowInitialized = "true";
      const stage = $("[data-coverflow-stage]", coverflow);
      if (!stage) return;
      const items = [...stage.children];
      if (!items.length) return;

      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      const delay = Math.max(1400, Number(coverflow.dataset.coverflowAutoplay) || 3200);
      let activeIndex = Math.floor(items.length / 2);
      let interval;
      let resumeTimer;
      let mediaPlaying = false;
      let inView = false;
      let pointerStart;

      const stop = () => {
        window.clearInterval(interval);
        interval = undefined;
      };

      const start = () => {
        stop();
        if (items.length < 2 || reducedMotion.matches || mediaPlaying || !inView || document.hidden) return;
        interval = window.setInterval(() => goTo(activeIndex + 1), delay);
      };

      const pauseTemporarily = () => {
        stop();
        window.clearTimeout(resumeTimer);
        if (!mediaPlaying) resumeTimer = window.setTimeout(start, 4000);
      };

      const relativePosition = (itemIndex) => {
        let difference = (itemIndex - activeIndex + items.length) % items.length;
        if (difference > items.length / 2) difference -= items.length;
        return difference;
      };

      const update = () => {
        items.forEach((item, itemIndex) => {
          const position = relativePosition(itemIndex);
          const visible = position >= -2 && position <= 2;
          item.dataset.coverflowPosition = visible ? String(position) : "hidden";
          item.classList.toggle("is-active", position === 0);
          item.setAttribute("aria-hidden", visible ? "false" : "true");
          item.tabIndex = position === 0 ? 0 : -1;
          if (position !== 0) {
            const video = $("video", item);
            if (video && !video.paused) video.pause();
            item.classList.remove("is-playing");
            $("[data-video-toggle]", item)?.setAttribute("aria-label", "Play video");
          }
        });
      };

      const goTo = (nextIndex, temporaryPause = false) => {
        activeIndex = (nextIndex + items.length) % items.length;
        mediaPlaying = false;
        update();
        if (temporaryPause) pauseTemporarily();
      };

      items.forEach((item, itemIndex) => {
        item.addEventListener("click", (event) => {
          if (itemIndex === activeIndex) return;
          event.preventDefault();
          goTo(itemIndex, true);
        });
      });

      $$("[data-coverflow-prev], [data-carousel-prev]", coverflow).forEach((button) => {
        button.addEventListener("click", () => goTo(activeIndex - 1, true));
      });
      $$("[data-coverflow-next], [data-carousel-next]", coverflow).forEach((button) => {
        button.addEventListener("click", () => goTo(activeIndex + 1, true));
      });

      stage.addEventListener("pointerdown", (event) => {
        pointerStart = { x: event.clientX, y: event.clientY };
      }, { passive: true });
      stage.addEventListener("pointerup", (event) => {
        if (!pointerStart) return;
        const deltaX = event.clientX - pointerStart.x;
        const deltaY = event.clientY - pointerStart.y;
        pointerStart = undefined;
        if (Math.abs(deltaX) < 35 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        goTo(activeIndex + (deltaX < 0 ? 1 : -1), true);
      }, { passive: true });
      stage.addEventListener("pointercancel", () => {
        pointerStart = undefined;
      }, { passive: true });
      stage.addEventListener("wheel", (event) => {
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY) || Math.abs(event.deltaX) < 12) return;
        event.preventDefault();
        goTo(activeIndex + (event.deltaX > 0 ? 1 : -1), true);
      }, { passive: false });

      coverflow.addEventListener("carousel:media-play", () => {
        mediaPlaying = true;
        stop();
      });
      coverflow.addEventListener("carousel:media-pause", () => {
        mediaPlaying = false;
        start();
      });

      const observer = new IntersectionObserver(([entry]) => {
        inView = entry.isIntersecting;
        if (inView) start();
        else stop();
      }, { threshold: 0.15 });
      observer.observe(coverflow);

      reducedMotion.addEventListener("change", start);
      document.addEventListener("visibilitychange", start);
      update();
    });
  };

  const initVideos = (scope = document) => {
    $$("[data-video-toggle]", scope).forEach((button) => {
      if (button.dataset.initialized) return;
      button.dataset.initialized = "true";
      button.addEventListener("click", async () => {
        const card = button.closest(".video-card");
        const video = $("video", card);
        if (!video) return;
        if (video.paused) {
          $$("video", button.closest(".video-feed")).forEach((otherVideo) => {
            if (otherVideo !== video) {
              otherVideo.pause();
              otherVideo.closest(".video-card")?.classList.remove("is-playing");
            }
          });
          await video.play();
          card.classList.add("is-playing");
          button.setAttribute("aria-label", "Pause video");
          button.closest("[data-carousel], [data-video-coverflow]")?.dispatchEvent(new CustomEvent("carousel:media-play"));
        } else {
          video.pause();
          card.classList.remove("is-playing");
          button.setAttribute("aria-label", "Play video");
          button.closest("[data-carousel], [data-video-coverflow]")?.dispatchEvent(new CustomEvent("carousel:media-pause"));
        }
      });
      $("video", button.closest(".video-card"))?.addEventListener("ended", () => {
        button.closest(".video-card")?.classList.remove("is-playing");
        button.setAttribute("aria-label", "Play video");
        button.closest("[data-carousel], [data-video-coverflow]")?.dispatchEvent(new CustomEvent("carousel:media-pause"));
      });
    });
  };

  const initProduct = (scope = document) => {
    $$("[data-product-gallery]", scope).forEach((gallery) => {
      if (gallery.dataset.galleryInitialized) return;
      gallery.dataset.galleryInitialized = "true";
      const mobileQuery = window.matchMedia("(max-width: 749px)");
      const main = $(".product-gallery__main", gallery);
      const buttons = $$("[data-media-target]", gallery);
      const mediaItems = $$("[data-media-id]", gallery);
      let scrollFrame;

      const setActive = (id, shouldScroll = false) => {
        buttons.forEach((item) => item.classList.toggle("is-active", item.dataset.mediaTarget === id));
        mediaItems.forEach((media) => {
          const active = media.dataset.mediaId === id;
          media.classList.toggle("is-active", active);
          if (!active) $("video", media)?.pause();
        });
        if (shouldScroll && mobileQuery.matches && main) {
          const target = mediaItems.find((media) => media.dataset.mediaId === id);
          if (target) {
            main.scrollTo({
              left: Math.max(0, target.offsetLeft - main.offsetLeft),
              behavior: "smooth"
            });
          }
        }
      };

      buttons.forEach((button) => {
        button.addEventListener("click", () => {
          setActive(button.dataset.mediaTarget, true);
        });
      });

      main?.addEventListener("scroll", () => {
        if (!mobileQuery.matches) return;
        window.cancelAnimationFrame(scrollFrame);
        scrollFrame = window.requestAnimationFrame(() => {
          let nearest = mediaItems[0];
          let distance = Number.POSITIVE_INFINITY;
          mediaItems.forEach((media) => {
            const nextDistance = Math.abs((media.offsetLeft - main.offsetLeft) - main.scrollLeft);
            if (nextDistance < distance) {
              nearest = media;
              distance = nextDistance;
            }
          });
          if (nearest) setActive(nearest.dataset.mediaId);
        });
      }, { passive: true });
    });

    $$("[data-product-form]", scope).forEach((form) => {
      const input = $('input[name="quantity"]', form);
      $("[data-quantity-minus]", form)?.addEventListener("click", () => {
        input.value = Math.max(1, Number(input.value || 1) - 1);
      });
      $("[data-quantity-plus]", form)?.addEventListener("click", () => {
        input.value = Number(input.value || 1) + 1;
      });
    });

    $$("[data-page-quantity-minus]", scope).forEach((button) => {
      button.addEventListener("click", () => {
        const input = $('input[type="number"]', button.parentElement);
        input.value = Math.max(0, Number(input.value || 1) - 1);
      });
    });
    $$("[data-page-quantity-plus]", scope).forEach((button) => {
      button.addEventListener("click", () => {
        const input = $('input[type="number"]', button.parentElement);
        input.value = Number(input.value || 1) + 1;
      });
    });
  };

  const initRecommendations = (scope = document) => {
    $$("[data-product-recommendations]", scope).forEach(async (section) => {
      if (section.dataset.loaded || !section.dataset.url) return;
      section.dataset.loaded = "true";
      try {
        const response = await fetch(section.dataset.url);
        if (!response.ok) return;
        const html = document.createElement("div");
        html.innerHTML = await response.text();
        const replacement = $("[data-product-recommendations]", html);
        if (replacement?.innerHTML.trim()) section.innerHTML = replacement.innerHTML;
      } catch {
        // Recommendations are optional and should not interrupt shopping.
      }
    });
  };

  const initCollection = (scope = document) => {
    const filters = $("[data-filters]", scope);
    $("[data-filter-open]", scope)?.addEventListener("click", () => {
      filters?.classList.add("is-open");
      lockPage(true);
    });
    $("[data-filter-close]", scope)?.addEventListener("click", () => {
      filters?.classList.remove("is-open");
      lockPage(false);
    });

    $("[data-sort-by]", scope)?.addEventListener("change", (event) => {
      const url = new URL(window.location.href);
      url.searchParams.set("sort_by", event.target.value);
      window.location.assign(url.toString());
    });
  };

  const init = (scope = document) => {
    initCountdowns(scope);
    initSliders(scope);
    initCarousels(scope);
    initVideoCoverflows(scope);
    initVideos(scope);
    initProduct(scope);
    initRecommendations(scope);
    initCollection(scope);
  };

  document.addEventListener("DOMContentLoaded", () => {
    initDrawers();
    initCart();
    init();
  });

  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();
